import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ExpenseForm from "./form";
import { CardHightlight, TableExpenses } from "@components";
import moment from "moment";
import Dialog from "@rio-cloud/rio-uikit/Dialog";
import DialogCreateType from "@components/Dialog/Create";
import DialogObservation from "@components/Dialog/Observation";
import {
  createBranch,
  createCategory,
  createSegments,
  createSubCategories,
  createSuppliers,
  createTransaction,
  deleteFile,
  deleteTransaction,
  editTransaction,
  exportTransactions,
  getDetailTransaction,
  getDRESummary,
  getFile,
  getTableData,
} from "@services/form";
import { Notification } from "@rio-cloud/rio-uikit";
import { FormState, useExpensesForm } from "./useExpensesForm";
import { Vehicle } from "@components/Table/Expenses/Table";
import { parseCurrencyBRL } from "@utils";
import DialogCreateCategory from "@components/Dialog/CreateCategory";
import { setEndDate, setStartDate, useGetStoredEndDate, useGetStoredStartDate } from "@store/date/dateSlice";
import { useAppDispatch } from "@store";
import { formatDateRange } from "@utils/dateRange";

interface ITableData {
  driverIds: string[];
  id: string;
  transactionDate: string;
  amount: number;
  branchName: string;
  segmentBussinessName: string;
  clientName: string;
  vehiclePlate: string;
  driverName: string;
  categoryName: string;
  subcategoryName: string;
  taxPercentage: string;
}

export type NameInputChange =
  | "date"
  | "value"
  | "branch"
  | "segment"
  | "supplier"
  | "trip"
  | "vehicle"
  | "driver"
  | "category"
  | "subcategory"
  | "taxes"
  | "observation";

interface Option {
  [x: string]: { placeholder: string; title: string };
}

const defaultOption = {
  open: false,
  type: "",
  title: "",
  value: "",
  placeholder: "",
};

const PAGE_SIZE = 10;

const ExpensesScreen = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openModal, setOpenModal] = useState(false);

  const startDate = useGetStoredStartDate();
  const endDate = useGetStoredEndDate();

  // Auto-open modal if add=expense query param is present
  useEffect(() => {
    if (searchParams.get("add") === "expense") {
      setOpenModal(true);
      searchParams.delete("add");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalExpenseRecords, setTotalExpenseRecords] = useState(0);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalExpenseAmount, setTotalExpenseAmount] = useState(0);
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteConfirmFileId, setDeleteConfirmFileId] = useState<string | null>(null);
  const [observation, setObservation] = useState({
    open: false,
    value: "",
  });
  const [file, setFile] = useState<File | null>(null);

  const [dialogOption, setDialogOption] = useState(defaultOption);
  const [tableData, setTableData] = useState<Vehicle[]>([]);
  const [fileName, setFileName] = useState("");

  const {
    branchOptions,
    segmentsOptions,
    supplierOptions,
    driversOptions,
    categoriesOptions,
    vehiclesOptions,
    subCategoriesOptions,
    form,
    initialDefaultForm,
    setForm,
    fetchBranchOptions,
    fetchSegments,
    fetchSupplier,
    fetchCategories,
    fetchSubCategories,
  } = useExpensesForm();

  const onCreateNewOption = (name: NameInputChange) => {
    // Operacao/Segmento usa dialog especifico com 2 campos
    // if (name === 'segment') {
    //   setShowOperationDialog(true);
    //   return;
    // }

    const opt: Option = {
      branch: {
        placeholder: "Qual o nome da nova filial?",
        title: "Nova filial",
      },
      supplier: {
        placeholder: "Qual o nome do novo(a) fornecedor?",
        title: "Novo(a) fornecedor",
      },
      category: {
        placeholder: "Qual a nova categoria?",
        title: "Nova categoria",
      },
      subcategory: {
        placeholder: "Qual a nova subcategoria?",
        title: "Nova subcategoria",
      },
      segment: {
        placeholder: "Qual o novo segmento?",
        title: "Novo segmento",
      },
    };

    setDialogOption({
      open: true,
      placeholder: opt[name]?.placeholder,
      title: opt[name]?.title,
      type: name,
      value: "",
    });
  };

  const handleSaveBranch = async (value: string) => {
    try {
      const response = await createBranch({
        name: value,
      });
      fetchBranchOptions();
      setForm({
        ...form,
        branch: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      setOpenModal(true);
      Notification.success("Filial criada com sucesso.");
    } catch (error) {
      Notification.error("Não foi possível criar a filial no momento.");
    }
  };

  const handleSaveOperation = async (value: string) => {
    try {
      const response = await createSegments(value);
      fetchSegments();
      setForm({
        ...form,
        segment: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      Notification.success("Segmento criado com sucesso.");
    } catch (error) {
      Notification.error("Nao foi possivel criar a operacao no momento.");
    }
  };

  const handleSaveSupplier = async (value: string) => {
    try {
      const response = await createSuppliers({
        name: value,
        type: "FORNECEDOR",
      });
      fetchSupplier();
      setForm({
        ...form,
        supplier: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      Notification.success("Fornecedor criado com sucesso.");
    } catch (error) {
      Notification.error("Não foi possível criar o fornecedor no momento.");
    }
  };

  const handleSaveCategory = async (value: string, selectValue?: string) => {
    try {
      const response = await createCategory({
        name: value,
        type: "DESPESA",
        expenseType: selectValue as "DIRETO" | "OPERACIONAL",
      });
      fetchCategories();
      setForm({
        ...form,
        category: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      setOpenModal(true);
      Notification.success("Categoria criada com sucesso.");
    } catch (error) {
      Notification.error("Não foi possível criar a categoria no momento.");
    }
  };

  const handleSaveSubCategory = async (value: string) => {
    try {
      const response = await createSubCategories(form.category, { name: value });
      fetchSubCategories();
      setForm({
        ...form,
        subcategory: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      Notification.success("Subcategoria criada com sucesso.");
    } catch (error) {
      Notification.error("Não foi possível criar a subcategoria no momento.");
    }
  };

  const handleSave = (value: string, selectValue?: string) => {
    if (dialogOption.type === "branch") {
      handleSaveBranch(value);
      return;
    }

    if (dialogOption.type === "supplier") {
      handleSaveSupplier(value);
      return;
    }

    if (dialogOption.type === "category") {
      handleSaveCategory(value, selectValue);
      return;
    }

    if (dialogOption.type === "subcategory") {
      handleSaveSubCategory(value);
      return;
    }
    if (dialogOption.type === "segment") {
      handleSaveOperation(value);
      return;
    }
  };

  const toVehicleRow = (item: ITableData): Vehicle => ({
    id: item.id.toString(),
    date: moment(item.transactionDate).format("DD/MM/YYYY"),
    amount: item.amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
    branch: item.branchName,
    segment: item.segmentBussinessName,
    customer: item.clientName,
    vehicle: item.vehiclePlate,
    driver: item.driverName,
    driverIds: item.driverIds || [],
    category: item.categoryName,
    subcategory: item.subcategoryName,
    taxes: `${item.taxPercentage}%`,
  });

  const deriveTotalRecords = (payload: any, fallback: number) => {
    const candidates = [
      payload?.totalElements,
      payload?.total,
      payload?.totalRecords,
      payload?.totalItems,
      payload?.totalReceitas,
      payload?.totalDespesas,
      payload?.meta?.totalElements,
      payload?.meta?.total,
      payload?.data?.totalElements,
      payload?.data?.total,
    ];

    const found = candidates.find((value) => typeof value === "number");

    return typeof found === "number" ? found : fallback;
  };

  const fetchTableData = useCallback(
    async (page = 0, append = false) => {
      const setLoading = append ? setIsLoadingMore : setIsLoadingTable;
      setLoading(true);

      try {
        const response = await getTableData(startDate, endDate, "DESPESA", {
          page,
          size: PAGE_SIZE,
        });

        const normalizedPayload = response?.data ?? response;
        const content = Array.isArray(normalizedPayload?.content) ? normalizedPayload.content : [];
        const formattedRows = content.map(toVehicleRow);

        setTableData((prev) => (append ? [...prev, ...formattedRows] : formattedRows));
        setTotalExpenseRecords(deriveTotalRecords(normalizedPayload, formattedRows.length));
        setCurrentPage(page);
      } catch (error) {
        Notification.error("Não foi possível carregar as despesas.");
      } finally {
        setLoading(false);
      }
    },
    [endDate, startDate]
  );

  const getDashboard = useCallback(async () => {
    const response = await getDRESummary(startDate, endDate);
    setTotalExpenseAmount(response.data.totalDespesas || 0);
  }, [endDate, startDate]);

  const fetchInitial = useCallback(async () => {
    await Promise.all([getDashboard(), fetchTableData(0)]);
  }, [fetchTableData, getDashboard]);

  const handleLoadMore = () => {
    if (isLoadingMore || tableData.length >= totalExpenseRecords) {
      return;
    }

    fetchTableData(currentPage + 1, true);
  };

  useEffect(() => {
    const loadData = async () => {
      setCurrentPage(0);
      setTableData([]);
      setTotalExpenseRecords(0);
      await fetchInitial();
    };

    loadData();
  }, [startDate, endDate, fetchInitial]);

  const saveNewExpense = async (data: FormState, callback?: () => void, dialogModal = true) => {
    try {
      await createTransaction(
        {
          amount: parseCurrencyBRL(data.value),
          branchId: Number(data.branch),
          categoryId: Number(data.category),
          description: data.observation,
          driverId: data.driver.length > 0 ? data.driver : null,
          vehicleId: data.vehicle ? data.vehicle : null,
          segmentBussinessId: Number(data.segment),
          subcategoryId: Number(data.subcategory),
          supplierClientId: Number(data.supplier),
          taxPercentage: 0,
          transactionDate: data.date,
          tripCode: data.trip,
          transactionType: "DESPESA",
        },
        file || undefined
      );

      if (dialogModal) {
        setOpenModal(false);
      }

      Notification.success("Despesa criada com sucesso.");
      callback?.();
      fetchInitial();
    } catch (error) {
      Notification.error("Não foi possível criar a despesa.");
    }
  };

  const handleEditExpense = async (id: string) => {
    try {
      const response = await getDetailTransaction(id);
      const expense = response.data;

      if (expense?.fileNameAttachmentUrl) {
        setFileName(expense?.fileNameAttachmentUrl);
      }

      setForm({
        date: expense.transactionDate?.split("T")[0] || "",
        value: expense.amount?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) || "",
        branch: expense.branchId?.toString() || "",
        segment: expense.segmentBussinessId?.toString() || "",
        supplier: expense.supplierClientId?.toString() || "",
        trip: expense?.tripCode,
        vehicle: expense.vehicleId || "",
        driver: expense.driverIds || (expense.driverId ? [expense.driverId] : []),
        category: expense.categoryId?.toString() || "",
        subcategory: expense.subcategoryId?.toString() || "",
        observation: expense.description || "",
      });

      setEditingExpenseId(id);
      setOpenModal(true);
    } catch (error) {
      Notification.error("Não foi possível carregar os dados da despesa.");
    }
  };

  const handleUpdateExpense = async (data: FormState, callback?: () => void) => {
    if (!editingExpenseId) return;

    try {
      await editTransaction(
        editingExpenseId,
        {
          amount: parseCurrencyBRL(data.value),
          branchId: Number(data.branch),
          categoryId: Number(data.category),
          description: data.observation,
          driverId: data.driver.length > 0 ? data.driver : null,
          vehicleId: data.vehicle ? data.vehicle : null,
          segmentBussinessId: Number(data.segment),
          subcategoryId: Number(data.subcategory),
          supplierClientId: Number(data.supplier),
          taxPercentage: 0,
          transactionDate: data.date,
          transactionType: "DESPESA",
        },
        file || undefined
      );

      setOpenModal(false);
      setEditingExpenseId(null);
      Notification.success("Despesa atualizada com sucesso.");
      callback?.();
      fetchInitial();
    } catch (error) {
      Notification.error("Não foi possível atualizar a despesa.");
    }
  };

  const handleDeleteExpense = async (id: string) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteFileExpense = async (id: string) => {
    setDeleteConfirmFileId(id);
  };

  const confirmDeleteFileExpense = async () => {
    if (!deleteConfirmFileId) return;

    try {
      await deleteFile(deleteConfirmFileId);
      setDeleteConfirmFileId(null);
      Notification.success("Arquivo excluído com sucesso.");
      fetchInitial();
    } catch (error) {
      Notification.error("Não foi possível excluir o arquivo.");
    }
  };

  const confirmDeleteExpense = async () => {
    if (!deleteConfirmId) return;

    try {
      await deleteTransaction(deleteConfirmId);
      setDeleteConfirmId(null);
      Notification.success("Despesa excluída com sucesso.");
      fetchInitial();
    } catch (error) {
      Notification.error("Não foi possível excluir a despesa.");
    }
  };

  const getDownloadFile = async (transactionId: string) => {
    try {
      const response = await getFile(transactionId);

      window.open(response.data.downloadUrl, "_blank");
      Notification.success("Comprovante baixado com sucesso.");
    } catch (error) {
      Notification.error("Não foi possível baixar o comprovante.");
    }
  };

  const handleDefaultForm = () => {
    setForm(initialDefaultForm);
    setEditingExpenseId(null);
  };

  const handleObservationDefault = () => {
    setObservation({
      open: false,
      value: "",
    });
  };

  const handleDownload = async () => {
    try {
      await exportTransactions(startDate, endDate, "DESPESA");
      Notification.success("Despesas exportadas com sucesso.");
    } catch (error) {
      Notification.error("Não foi possível exportar as despesas.");
    }
  };

  const handleObservation = async (id: string) => {
    try {
      const response = await getDetailTransaction(id);
      setObservation({
        open: true,
        value: response?.data?.description,
      });
    } catch (error) {
      Notification.error("Não foi possível obter os detalhes da despesa.");
      handleObservationDefault();
    }
  };

  const handleSaveAndCreateNew = () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      return;
    }
    saveNewExpense(form, handleDefaultForm, false);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!form.date) newErrors.date = "Informe a data";
    if (!form.value) newErrors.value = "Informe o valor";
    if (!form.branch) newErrors.branch = "Selecione a filial";
    if (!form.category) newErrors.category = "Selecione a categoria";
    return newErrors;
  };

  return (
    <div className="padding-20 display-flex flex-column gap-20 min-height-100pct">
      <div className="display-flex width-100pct gap-50">
        <div className="width-50pct">
          <CardHightlight
            icon="rioglyph rioglyph-calendar"
            label="Período"
            value={formatDateRange(new Date(startDate + "T00:00:00"), new Date(endDate + "T00:00:00"))}
            direction="LEFT"
          />
        </div>
        <div className="width-50pct">
          <CardHightlight
            icon="rioglyph rioglyph-arrow-down text-size-h1 text-color-danger"
            label="Valor total das despesas"
            value={totalExpenseAmount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
            direction="RIGHT"
          />
        </div>
      </div>
      <div className="flex-1-1 height-100pct">
        <TableExpenses
          data={tableData}
          totalData={totalExpenseRecords}
          startDate={startDate}
          endDate={endDate}
          handleStartDate={(date) => dispatch(setStartDate(date))}
          handleEndDate={(date) => dispatch(setEndDate(date))}
          viewType="TABLE"
          onLoadMore={handleLoadMore}
          handleRefresh={fetchInitial}
          handleDownload={handleDownload}
          openModal={() => {
            setFileName("");
            setOpenModal(true);
          }}
          onEdit={handleEditExpense}
          onDelete={handleDeleteExpense}
          onDeleteFile={handleDeleteFileExpense}
          onGetFile={getDownloadFile}
          onObservation={handleObservation}
          isLoadingMore={isLoadingMore}
          isLoadingTable={isLoadingTable}
        />

        <Dialog
          show={openModal && !dialogOption.open}
          title={
            <span className="text-medium text-size-16">{editingExpenseId ? "Editar despesa" : "Nova despesa"}</span>
          }
          body={
            <ExpenseForm
              branchs={branchOptions}
              segments={segmentsOptions}
              suppliers={supplierOptions}
              drivers={driversOptions}
              categories={categoriesOptions}
              vehicles={vehiclesOptions}
              subCategories={subCategoriesOptions}
              fileName={fileName}
              changeFileName={(filename) => setFileName(filename)}
              form={form}
              setForm={setForm}
              openFormModal={(toggle) => {
                handleDefaultForm();
                setOpenModal(toggle);
              }}
              onCreateNewOption={onCreateNewOption}
              onSave={(formData) =>
                editingExpenseId
                  ? handleUpdateExpense(formData, handleDefaultForm)
                  : saveNewExpense(formData, handleDefaultForm)
              }
              isEdit={!!editingExpenseId}
              handleSubmitNew={handleSaveAndCreateNew}
              handleFile={(file) => setFile(file)}
              handleClearFile={() => setFile(null)}
            />
          }
          onClose={() => {
            setOpenModal(false);
            handleDefaultForm();
          }}
          className="exampleDialog"
          showCloseButton
          bsSize="md"
        />

        <DialogCreateType
          title={dialogOption.title}
          show={dialogOption.type !== "category" && dialogOption.open}
          placeholder={dialogOption.placeholder}
          onClose={() => {
            setOpenModal(true);
            setDialogOption(defaultOption);
          }}
          onSave={handleSave}
        />

        <DialogCreateCategory
          title={dialogOption.title}
          show={dialogOption.type === "category" && dialogOption.open}
          name={dialogOption.type}
          placeholder={dialogOption.placeholder}
          onClose={() => {
            setOpenModal(true);
            setDialogOption(defaultOption);
          }}
          onSave={handleSave}
        />

        {/* <DialogCreateOperation
          show={showOperationDialog}
          onClose={() => { setShowOperationDialog(false); setOpenModal(true); }}
          onSave={handleSaveOperation}
        /> */}

        <Dialog
          show={deleteConfirmId !== null}
          title={<span className="text-medium text-size-16">Excluir despesa</span>}
          body={
            <div className="padding-20">
              <p>Tem certeza que deseja excluir esta despesa?</p>
              <p className="text-color-danger">Esta ação não pode ser desfeita.</p>
            </div>
          }
          footer={
            <div className="display-flex gap-10 justify-content-end">
              <button className="btn btn-default" onClick={() => setDeleteConfirmId(null)}>
                Cancelar
              </button>
              <button className="btn btn-danger" onClick={confirmDeleteExpense}>
                Excluir
              </button>
            </div>
          }
          onClose={() => setDeleteConfirmId(null)}
          className="exampleDialog"
          showCloseButton
          bsSize="sm"
        />

        <Dialog
          show={deleteConfirmFileId !== null}
          title={<span className="text-medium text-size-16">Excluir comprovante</span>}
          body={
            <div className="padding-20">
              <p>Tem certeza que deseja excluir este comprovante?</p>
              <p className="text-color-danger">Esta ação não pode ser desfeita.</p>
            </div>
          }
          footer={
            <div className="display-flex gap-10 justify-content-end">
              <button className="btn btn-default" onClick={() => setDeleteConfirmFileId(null)}>
                Cancelar
              </button>
              <button className="btn btn-danger" onClick={confirmDeleteFileExpense}>
                Excluir
              </button>
            </div>
          }
          onClose={() => setDeleteConfirmFileId(null)}
          className="exampleDialog"
          showCloseButton
          bsSize="sm"
        />

        <DialogObservation
          onClose={handleObservationDefault}
          show={observation.open}
          title="Observações"
          value={observation.value}
        />
      </div>
    </div>
  );
};

export default ExpensesScreen;
